
import InformationMessage from '@/components/InformationMessage.vue'
import MainSlider from '@/components/MainSlider.vue'
import HomeTrustBar from '@/components/HomeTrustBar.vue'
import SkeepersCarousel from '@/components/SkeepersCarousel.vue'
import LazyHydrate from 'vue-lazy-hydration'
import { COOKIE_DURATION, cookieOpts } from '@/utils/cookieOptions'
import { buildOgLocaleMeta } from '@/utils/seo'
import { storeNode } from '@/utils/storeStructuredData'
import { storeFacade } from '@/utils/storeFacade'
import { clockChangeCardFor } from '@/utils/clockChange'
import {
  PRODUCT_TAB_RECENTLY_VIEWED,
  PRODUCT_TAB_SELECTION,
  initialProductTab,
} from '@/utils/home'
import { lightingGuideAvailable } from '@/utils/lightingGuide'
import {
  H7_COMPARISON_ARTICLE,
  LIGHTING_CHECK_GUIDE,
} from '@/utils/staticBlogArticles'
import { keepHiddenContentOutOfTabOrder } from '@/utils/hiddenFocus'
import HomeSection from '@/components/HomeSection.vue'

const HP_TONES = {
  selection: 'mist',
  vehicles: 'white',
  reviews: 'white',
  community: 'mist',
  partners: 'white',
  comparison: 'mist',
  blog: 'white',
  deals: 'mist',
  faq: 'white',
  seo: 'mist',
}

export default {
  name: 'Index',
  components: {
    LazyHydrate,
    InformationMessage,
    MainSlider,
    HomeTrustBar,
    SkeepersCarousel,
    HomeSection,
    ClientsReviewsSlider: () => import('@/components/ClientsReviewsSlider'),
    ProductSliderListItem: () => import('@/components/ProductSliderListItem'),
    ArticleList: () => import('@/components/ArticleList'),
    FaqList: () => import('@/components/FaqList'),
    HomeVehicleTypes: () => import('@/components/HomeVehicleTypes'),
    HomeBrandsBanner: () => import('@/components/HomeBrandsBanner'),
    HomeSocialProof: () => import('@/components/HomeSocialProof'),
    HomeLedBlueprint: () => import('@/components/HomeLedBlueprint'),
  },
  props: {
    domain: Object,
    itemsPerSlide: {
      type: Number,
      default: 4,
    },
    isFromSlider: {
      default: false,
      type: Boolean,
    },
    productMaxWidth: {
      default: null,
      type: Number,
    },
  },
  data() {
    return {
      hydrateWhenVisible: { rootMargin: '300px 0px' },
      articles_list: [],
      questions: [],
      partnersWall: [],
      partnersWallMeta: null,
      deals: [],
      reviews: [],
      agm_suggestions: [],
      agm_suggestions_loading: true,
      recently_viewed: [],
      recently_viewed_loading: false,
      activeProductTab: PRODUCT_TAB_SELECTION,
      productTabs: {
        selection: PRODUCT_TAB_SELECTION,
        recentlyViewed: PRODUCT_TAB_RECENTLY_VIEWED,
      },
      reviewSummary: null,
      skeepersStats: null,
      allBrands: [],
      localizedSlugs: { categories: {}, brands: {} },
      hiddenFocusObserver: null,
    }
  },

  async fetch() {
    await Promise.allSettled([
      this.$questionService.fetchHome().then(({ data }) => {
        this.questions = data
      }),
      this.$articleService.fetchHome().then(({ data }) => {
        this.setArticlesList(data)
      }),
      this.$reviewsService.fetchHomeFeatured().then(({ data }) => {
        this.reviews = data
      }),
      this.$verifiedReviewsService.fetchSummarySkeepers().then(response => {
        this.reviewSummary = response.review_summary
        this.skeepersStats = {
          rating: response.average || null,
          count: response.count_total_since_creation || null,
        }
      }),
      this.$brandService
        .fetch({
          'filter[types]': 'vehicle',
          'filter[has_active_vehicles]': 0,
          'fields[brands]': 'id,name,slug',
          include: 'image',
          sort: 'name',
          limit: this.$itemsRequestLimit,
        })
        .then(({ data }) => {
          this.allBrands = (data || [])
            .filter(brand => brand.image)
            .map(brand => ({
              id: brand.id,
              name: brand.name,
              slug: brand.slug,
              image: {
                url: brand.image.url,
                url_template: brand.image.url_template,
                extension: brand.image.extension,
              },
            }))
        }),
      this.$productService
        .fetchHomeSuggestions({ light: 1 })
        .then(({ data }) => {
          this.agm_suggestions = data || []
          this.agm_suggestions_loading = false
        }),
      this.$store.dispatch('setDomains'),
      this.$localizedSlugService
        .fetch({
          categories: '1,2,3,4,15,16,18,19,350',
          brands: '20,55,53,40',
        })
        .then(data => {
          this.localizedSlugs = {
            categories: data?.categories || {},
            brands: data?.brands || {},
          }
        }),
    ])
  },

  head() {
    return {
      title: this.$t('pages.index.metaTitle'),
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: this.$t('pages.index.metaDescription'),
        },
        { hid: 'og:type', property: 'og:type', content: 'website' },
        {
          hid: 'og:url',
          property: 'og:url',
          content: this.$config.url + this.localePath({ name: 'index' }),
        },
        {
          hid: 'og:title',
          property: 'og:title',
          content: this.$t('pages.index.metaOgTitle'),
        },
        {
          hid: 'og:description',
          property: 'og:description',
          content: this.$t('pages.index.metaOgDescription'),
        },
        {
          hid: 'og:site_name',
          property: 'og:site_name',
          content: 'AGM Vision',
        },
        ...buildOgLocaleMeta(this.$i18n.locale),
        {
          hid: 'og:image',
          property: 'og:image',
          content: `${this.$config.url}/logo_og.png`,
        },
        {
          hid: 'og:image:width',
          property: 'og:image:width',
          content: '512',
        },
        {
          hid: 'og:image:height',
          property: 'og:image:height',
          content: '512',
        },
        {
          hid: 'og:image:alt',
          property: 'og:image:alt',
          content: 'AGM VISION - Ampoules LED pour voiture, moto et camion',
        },
        {
          hid: 'twitter:card',
          name: 'twitter:card',
          content: 'summary_large_image',
        },
        {
          hid: 'twitter:title',
          name: 'twitter:title',
          content: this.$t('pages.index.metaOgTitle'),
        },
        {
          hid: 'twitter:description',
          name: 'twitter:description',
          content: this.$t('pages.index.metaOgDescription'),
        },
        {
          hid: 'twitter:image',
          name: 'twitter:image',
          content: `${this.$config.url}/logo_og.png`,
        },
      ],
      link: [
        {
          hid: 'i18n-can',
          rel: 'canonical',
          href: this.$config.url + this.localePath({ name: 'index' }),
        },
      ],
      __dangerouslyDisableSanitizers: ['script'],
      script: [
        {
          innerHTML: JSON.stringify(this.structuredData),
          type: 'application/ld+json',
        },
      ],
    }
  },
  computed: {
    slideCount() {
      return this.itemsPerSlide
    },
    identityRating() {
      const { rating, count } = this.skeepersStats || {}
      if (!rating || !count) return null
      const locale = this.$i18n.locale
      return {
        value: Number(rating),
        rating: Number(rating).toLocaleString(locale, {
          maximumFractionDigits: 1,
        }),
        count: Number(count).toLocaleString(locale),
      }
    },
    productTabList() {
      return [
        {
          key: this.productTabs.recentlyViewed,
          label: this.$t('pages.index.recentlyViewed'),
        },
        {
          key: this.productTabs.selection,
          label: this.$t('pages.index.selectionMoment'),
        },
      ]
    },
    brandsLink() {
      return {
        to: this.localePath({ name: 'marques-vehicules' }),
        label: this.$t('components.footerBottom.allVehicleBrandsLink'),
      }
    },
    dealsLink() {
      return {
        to: this.localePath({ name: 'bonnes-affaires' }),
        label: this.$t('pages.index.seeAllDeals'),
      }
    },
    articlesLink() {
      return {
        to: this.localePath({ name: 'blog-led-accessoires-vehicules' }),
        label: this.$t('pages.index.seeAllArticles'),
      }
    },
    partnersWallRows() {
      if (!this.partnersWall.length) return [[], []]
      const half = Math.ceil(this.partnersWall.length / 2)
      return [
        this.partnersWall.slice(0, half),
        this.partnersWall.slice(half),
      ].map(row => [...row, ...row])
    },
    clockChangeCard() {
      return clockChangeCardFor(this.$store.state, this.$i18n.locale)
    },
    showProductTabs() {
      return this.recently_viewed.length > 0
    },
    structuredData() {
      const urlOrigin = process.client
        ? window.location.origin
        : this.$config.url
      const urlPath = process.client ? window.location.pathname : ''

      return {
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'Organization',
            '@id': `${this.$config.url}/#organization`,
            name: 'AGM Vision',
            url: this.$config.url,
            logo: `${this.$config.url}/logo_og.png`,
            sameAs: [
              'https://www.facebook.com/agmvision/',
              'https://www.instagram.com/agm_vision/',
              'https://www.tiktok.com/@agmvision',
              'https://www.youtube.com/@agmvision',
            ],
          },
          {
            '@type': 'WebSite',
            name: 'AGM Vision',
            url: this.$config.url,
            publisher: {
              '@id': `${this.$config.url}/#organization`,
            },
            potentialAction: {
              '@type': 'SearchAction',
              target: `${urlOrigin}${urlPath}?q={search_term_string}`,
              'query-input': 'required name=search_term_string',
            },
          },
          {
            ...storeNode({
              baseUrl: this.$config.url,
              url:
                this.$config.url +
                this.localePath({ name: 'magasin-excideuil' }),
              image: this.$config.url + storeFacade.large,
              openingHours: this.$store.state.store_opening_hours,
              parkingName: this.$t('pages.magasinExcideuil.freeParking'),
            }),
            aggregateRating: {
              '@type': 'AggregateRating',
              ratingValue: String(this.skeepersStats?.rating ?? '4.9'),
              reviewCount: String(this.skeepersStats?.count ?? '3127'),
            },
          },
        ],
      }
    },
    seoLinks() {
      return {
        kitsAndLedBulbs: this.localePath({
          name: 'categories-domain',
          params: {
            domain: this.localizedCategorySlug(1, 'kits-et-ampoules-led-1'),
          },
        }),
        tuningAutomobile: this.localePath({
          name: 'categories-domain',
          params: {
            domain: this.localizedCategorySlug(
              350,
              'tuning-led-et-feux-de-jour-1-350'
            ),
          },
        }),
        bmw: this.localePath({
          name: 'marques-vehicules-slug',
          params: { slug: this.localizedBrandSlug(20, 'bmw-20') },
        }),
        audi: this.localePath({
          name: 'marques-vehicules-slug',
          params: { slug: this.localizedBrandSlug(55, 'audi-55') },
        }),
        peugeot: this.localePath({
          name: 'marques-vehicules-slug',
          params: { slug: this.localizedBrandSlug(40, 'peugeot-40') },
        }),
      }
    },
  },
  mounted() {
    this.hiddenFocusObserver = keepHiddenContentOutOfTabOrder(this.$el)
    this.getAgmSuggestions()
    this.getRecentlyViewed()
    this.getFeaturedPartners()
    this.getDeals()

    if (this.$route.query.newsletter_secret_key) {
      this.$newsletterSubService
        .enableGuest(this.$route.query.newsletter_secret_key)
        .then(() => {
          this.$trackNewsletterConfirmed()
          this.$swalCustomSuccess(
            this.$t('pages.index.newsletterSubscriptionSuccessTitle'),
            this.$t('pages.index.newsletterSubscriptionSuccessMessage')
          )
        })
        .catch(error => {
          this.$swalErrorMessage(error)
        })
    }

    if (this.$route.query.loginToUser) {
      this.$auth.setUserToken(this.$route.query.loginToUser).then(() => {
        this.$nuxtCookies.set(
          'admin_logged_in_user',
          true,
          cookieOpts(COOKIE_DURATION.MONTH)
        )
        window.location.replace(this.$config.url + '/compte')
      })
    }
  },

  created() {
    this.$configurationService
      .getPublic('home-banner-url')
      .then(() => {})
      .catch(() => {})

    this.$store.dispatch('setMessage').catch(() => {})

    this.$store.dispatch('loadVehicleSearchHistory')
  },
  beforeDestroy() {
    if (this.hiddenFocusObserver) this.hiddenFocusObserver.disconnect()
  },
  methods: {
    tone(section) {
      return HP_TONES[section] || 'plain'
    },
    localizedCategorySlug(id, fallback) {
      return this.localizedSlugs.categories[id] || fallback
    },
    localizedBrandSlug(id, fallback) {
      return this.localizedSlugs.brands[id] || fallback
    },
    getRecentlyViewed() {
      if (!this.$auth.loggedIn) return
      this.recently_viewed_loading = true
      this.$productService
        .getRecentlyVisited({ limit: 20 })
        .then(({ data }) => {
          this.recently_viewed = data || []
          this.activeProductTab = initialProductTab(this.recently_viewed)
        })
        .catch(() => {})
        .finally(() => {
          this.recently_viewed_loading = false
        })
    },
    getAgmSuggestions() {
      const ids = this.agm_suggestions.map(product => product.id)
      this.agm_suggestions_loading = !ids.length
      this.$productService
        .fetchHomeSuggestions(ids.length ? { ids: ids.join(',') } : undefined)
        .then(({ data }) => {
          this.agm_suggestions = data
        })
        .catch(() => {})
        .finally(() => {
          this.agm_suggestions_loading = false
        })
    },

    setArticlesList(data) {
      const h7ComparisonArticle = {
        id: 'h7-comparison',
        slug: 'comparatif-meilleures-ampoules-led-h7',
        title: this.$t(
          'pages.blogLedAccessoiresVehicules.comparatifMeilleuresAmpoulesLedH7.title',
          {
            year: new Date().getFullYear(),
          }
        ),
        subtitle: this.$t(
          'pages.blogLedAccessoiresVehicules.comparatifMeilleuresAmpoulesLedH7.subtitle'
        ),
        article_type_id: 2,
        created_at: H7_COMPARISON_ARTICLE.datePublished,
        bumped_at: H7_COMPARISON_ARTICLE.dateModified,
        images: [
          {
            title: this.$t(
              'pages.blogLedAccessoiresVehicules.comparatifMeilleuresAmpoulesLedH7.title',
              {
                year: new Date().getFullYear(),
              }
            ),
            url: '/ampoule-led-h7-voiture.jpg',
            isPlaceholder: true,
          },
        ],
        _isCustomArticle: true,
      }

      const lightingGuideTitle = this.$t(
        'pages.blogLedAccessoiresVehicules.verifierEclairageVoiture.title'
      )
      const lightingGuideArticle = lightingGuideAvailable(this.$i18n.locale)
        ? {
            id: 'lighting-check-guide',
            slug: 'verifier-eclairage-voiture',
            title: lightingGuideTitle,
            subtitle: this.$t(
              'pages.blogLedAccessoiresVehicules.verifierEclairageVoiture.metaDescription'
            ),
            article_type_id: 2,
            created_at: LIGHTING_CHECK_GUIDE.datePublished,
            bumped_at: LIGHTING_CHECK_GUIDE.dateModified,
            images: [
              {
                title: lightingGuideTitle,
                url: '/verifier-feu-phare-voiture.jpg',
                isPlaceholder: true,
              },
            ],
            _isCustomArticle: true,
          }
        : null

      const h7ArticleExists = data.some(
        article => article.slug === 'comparatif-meilleures-ampoules-led-h7'
      )

      if (!h7ArticleExists) {
        this.articles_list = [
          lightingGuideArticle,
          h7ComparisonArticle,
          ...data,
        ]
          .filter(Boolean)
          .slice(0, 4)
      } else {
        const filteredData = data.filter(
          article => article.slug !== 'comparatif-meilleures-ampoules-led-h7'
        )
        const existingH7Article = data.find(
          article => article.slug === 'comparatif-meilleures-ampoules-led-h7'
        )
        this.articles_list = [
          lightingGuideArticle,
          existingH7Article,
          ...filteredData,
        ]
          .filter(Boolean)
          .slice(0, 4)
      }
    },

    getDeals() {
      this.$goodDealService
        .fetchHome()
        .then(({ data }) => {
          this.deals = (data || []).filter(deal => deal.products_count > 0)
        })
        .catch(() => {})
    },

    getFeaturedPartners() {
      this.$partnerService
        .fetchHomeWall()
        .then(({ data, meta }) => {
          this.partnersWall = data || []
          this.partnersWallMeta = meta || null
        })
        .catch(() => {})
    },
  },
}
